import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { Event, EventType } from "../../../types/event";
import { BottomSheet } from "../../ui/BottomSheet";
import { AddEventChooser, type ChooserCardId } from "./AddEventChooser";
import styles from "./AddEventSheet.module.css";
import { FormHeaderContext, type FormHeaderState } from "./FormHeaderContext";
import { AdminForm } from "./forms/AdminForm";
import { AppointmentTestForm } from "./forms/AppointmentTestForm";
import { CycleForm } from "./forms/CycleForm";
import { MedicationForm } from "./forms/MedicationForm";

type Step = "chooser" | "form";
export type AddEventMode = "create" | "edit";

const TITLE_ID = "add-event-sheet-title";

/**
 * Map an EventType (edit-mode entry) onto a chooser-card identity so the
 * sheet can use a single state value for both flows.
 */
function eventTypeToCard(type: EventType): ChooserCardId {
	if (type === "appointment" || type === "test") return "appointmentTest";
	return type;
}

/** Visual category used for type-coloured Save button + TypeBar. */
function cardVisualCategory(
	card: ChooserCardId,
): "test-appt" | "meds" | "cycle" | "admin" | "other" {
	if (card === "appointmentTest") return "test-appt";
	if (card === "medication") return "meds";
	if (card === "cycle") return "cycle";
	if (card === "admin") return "admin";
	return "other";
}

export interface AddEventSheetProps {
	isOpen: boolean;
	onClose: () => void;
	/** Defaults to 'create'. Edit mode skips chooser (D-18). */
	initialMode?: AddEventMode;
	/** When set, opens directly to the form step (D-18 edit case). */
	initialType?: EventType;
	/** Edit-mode pre-fill — consumed by form bodies wired in Plans 07-08. */
	initialValues?: Event;
}

/**
 * Phase 37 — Add Event bottom sheet (single sheet, chooser↔form state machine).
 *
 * D-15: One BottomSheet, body swaps based on `step`. Chooser is the entry point
 * in create mode; edit mode opens directly to the form (skipping chooser per D-18).
 *
 * Header layout:
 *   - Chooser step: title + close (X) + chooser subtitle below
 *   - Form step:    Back chevron + label | centered title | Save (right, type-coloured)
 *
 * Forms register their isValid / isPending / onSave / mode into FormHeaderContext via
 * EventFormShell so the header Save button reflects current validation. The 4px
 * type-coloured strip pins to the bottom of the header section, replacing the
 * BottomSheet's default grey border in form mode.
 */
export function AddEventSheet({
	isOpen,
	onClose,
	initialMode = "create",
	initialType,
	initialValues,
}: AddEventSheetProps) {
	const { t } = useClientTranslations("track");
	const initialCard = initialType ? eventTypeToCard(initialType) : null;
	const [step, setStep] = useState<Step>(initialCard ? "form" : "chooser");
	const [selectedCard, setSelectedCard] = useState<ChooserCardId | null>(
		initialCard,
	);
	const [formState, setFormState] = useState<FormHeaderState | null>(null);

	// Reset state when the sheet opens for a new session — keeps create vs edit clean.
	useEffect(() => {
		if (isOpen) {
			const next = initialType ? eventTypeToCard(initialType) : null;
			setStep(next ? "form" : "chooser");
			setSelectedCard(next);
			setFormState(null);
		}
	}, [isOpen, initialType]);

	const handleSelectCard = (id: ChooserCardId) => {
		setSelectedCard(id);
		setStep("form");
	};

	const handleBack = () => {
		// Only available in create mode (edit mode has no chooser to return to).
		setStep("chooser");
		setSelectedCard(null);
		setFormState(null);
	};

	const isFormStep = step === "form" && selectedCard !== null;
	const isCreate = initialMode === "create";
	const isChooserStep = !isFormStep;

	// Title key — for edit mode we use the actual EventType (initialType), for
	// create mode we use the chooser-card id so "appointmentTest" gets its own
	// "Add Test / Appointment" title.
	const titleKey: string = isFormStep
		? isCreate
			? `event.add.${selectedCard}.title`
			: `event.edit.title.${initialType ?? selectedCard}`
		: "event.add.chooser.title";

	const visualCategory = selectedCard ? cardVisualCategory(selectedCard) : null;

	const saveDisabled = !formState?.isValid || formState.isPending;
	const saveLabelKey = formState
		? formState.mode === "create"
			? formState.isPending
				? "event.add.actions.saving"
				: "event.add.actions.save"
			: formState.isPending
				? "event.edit.actions.saving"
				: "event.edit.actions.save"
		: "event.add.actions.save";

	const sheetHeader = (
		<div
			className={`${styles.headerSection} ${isFormStep ? styles.headerSectionForm : ""}`}
		>
			<div
				className={`${styles.headerRow} ${isFormStep ? styles.headerRowForm : ""}`}
			>
				{isFormStep && isCreate ? (
					<button type="button" className={styles.backBtn} onClick={handleBack}>
						<ChevronLeftIcon className={styles.backIcon} aria-hidden="true" />
						<span className={styles.backLabel}>
							{t("event.add.actions.back")}
						</span>
					</button>
				) : null}
				<h2 className={styles.title} id={TITLE_ID}>
					{t(titleKey)}
				</h2>
				{isFormStep ? (
					<button
						type="button"
						className={`${styles.saveBtn} ${visualCategory ? styles[`saveBtn--${visualCategory}`] : ""}`}
						onClick={() => formState?.onSave()}
						disabled={saveDisabled}
						aria-disabled={saveDisabled}
					>
						{t(saveLabelKey)}
					</button>
				) : (
					<button
						type="button"
						className={styles.headerBtn}
						onClick={onClose}
						aria-label={t("sheet.close")}
					>
						<XMarkIcon className={styles.headerIcon} aria-hidden="true" />
					</button>
				)}
			</div>
			{isChooserStep && isCreate ? (
				<p className={styles.subtitle}>{t("event.add.chooser.subtitle")}</p>
			) : null}
			{isFormStep && visualCategory ? (
				<div
					className={`${styles.typeBar} ${styles[`typeBar--${visualCategory}`]}`}
					aria-hidden="true"
				/>
			) : null}
		</div>
	);

	// In edit mode the AppointmentTest form receives the actual formType from
	// initialType so the subtype dropdown + save type are correct. In create
	// mode (selected via the merged chooser card), default to "appointment" —
	// the form renders an internal Appointment/Test pill for the user to switch.
	const apptTestFormType: "appointment" | "test" =
		initialType === "test" ? "test" : "appointment";

	return (
		<FormHeaderContext.Provider value={{ formState, setFormState }}>
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				ariaLabelledBy={TITLE_ID}
				header={sheetHeader}
				panelHeight="tall"
			>
				{isFormStep && selectedCard ? (
					<div className={styles.formStep} data-testid="add-event-form-slot">
						{selectedCard === "appointmentTest" ? (
							<AppointmentTestForm
								formType={apptTestFormType}
								mode={initialMode}
								initialValues={initialValues}
								onClose={onClose}
								allowFormTypeToggle={isCreate}
							/>
						) : selectedCard === "medication" ? (
							<MedicationForm
								mode={initialMode}
								initialValues={initialValues}
								onClose={onClose}
							/>
						) : selectedCard === "cycle" ? (
							<CycleForm
								mode={initialMode}
								initialValues={initialValues}
								onClose={onClose}
							/>
						) : selectedCard === "admin" || selectedCard === "other" ? (
							<AdminForm
								mode={initialMode}
								initialValues={initialValues}
								onClose={onClose}
							/>
						) : null}
					</div>
				) : (
					<AddEventChooser onSelectCard={handleSelectCard} />
				)}
			</BottomSheet>
		</FormHeaderContext.Provider>
	);
}
